/**
 * ERRORS-1 — map raw failures (thrown errors, API envelopes, bridge close codes, recording
 * rows) onto the catalogue's stable codes. Pure and platform-agnostic: browser errors are
 * duck-typed by `name` so this package keeps no DOM dependency.
 */
import { ScribeApiError, ScribeNetworkError } from "../client/errors.js";
/**
 * Thrown by capture code for conditions that have no native exception (the device went away
 * mid-recording, the platform lacks an API). Carries its catalogue code directly.
 */
export class CaptureError extends Error {
    name = "CaptureError";
    code;
    constructor(code, message) {
        super(message ?? code);
        this.code = code;
    }
}
function errorName(err) {
    if (typeof err !== "object" || err === null)
        return undefined;
    const name = err.name;
    return typeof name === "string" ? name : undefined;
}
/**
 * The capture-stage code for `err` (a `getUserMedia` / `AudioContext` / worklet failure), or
 * `null` when `err` is not recognizably a capture failure — callers then fall through to the
 * network/API mappers. Names per the Media Capture spec plus the legacy Chrome/Firefox
 * aliases still seen in the wild.
 */
export function classifyCaptureError(err) {
    if (err instanceof CaptureError)
        return err.code;
    switch (errorName(err)) {
        case "NotAllowedError":
        case "PermissionDeniedError":
        case "SecurityError":
            return "mic_permission_denied";
        case "NotFoundError":
        case "DevicesNotFoundError":
        case "OverconstrainedError":
            return "mic_not_found";
        case "NotReadableError":
        case "TrackStartError":
            return "mic_in_use";
        case "NotSupportedError":
            return "capture_unsupported";
        default:
            return null;
    }
}
/** Bridge close-code mapping (plus the client-only `bridge_not_configured`) → catalogue. */
export function problemCodeForStreamingError(code) {
    switch (code) {
        case "unauthorized":
            return "session_expired";
        case "forbidden":
            return "access_denied";
        case "not_found":
            return "recording_not_found";
        case "bad_request":
            return "invalid_request";
        case "budget_exceeded":
            return "budget_exceeded";
        // 1011 — deliberately NOT a claim that audio was lost: the bridge also sends it when the
        // finalize tail fails after the master was written (docs/outstanding-work.md §2.10).
        case "engine_error":
            return "live_engine_error";
        case "network":
            return "connection_lost";
        case "protocol":
            return "server_protocol_error";
        case "bridge_not_configured":
            return "capture_service_unavailable";
    }
}
/** Server `error.code` values that map one-to-one onto a catalogue code. */
const API_CODES = {
    budget_exceeded: "budget_exceeded",
    transcription_in_flight: "transcription_in_flight",
    pipeline_unavailable: "pipeline_unavailable",
    rate_limited: "rate_limited",
    unauthorized: "session_expired",
    forbidden: "access_denied",
    not_found: "recording_not_found",
};
/**
 * REST failure → catalogue code. Prefers the server's own `error.code`, then the HTTP status.
 * `online: false` (the platform's connectivity flag, when known) turns a transport failure
 * into `offline` rather than the vaguer `connection_lost`.
 */
export function problemCodeForApiError(err, online) {
    if (err instanceof ScribeApiError) {
        const byCode = err.code ? API_CODES[err.code] : undefined;
        if (byCode)
            return byCode;
        if (err.status === 401)
            return "session_expired";
        if (err.status === 403)
            return "access_denied";
        if (err.status === 404)
            return "recording_not_found";
        if (err.status === 409)
            return "transcription_in_flight";
        if (err.status === 429)
            return "rate_limited";
        if (err.status === 400 || err.status === 422)
            return "invalid_request";
        return "unknown";
    }
    if (err instanceof ScribeNetworkError || errorName(err) === "TypeError") {
        return online === false ? "offline" : "connection_lost";
    }
    // MSAL's InteractionRequiredAuthError (and its subclasses) — the sign-in needs a human.
    if (errorName(err) === "InteractionRequiredAuthError")
        return "session_expired";
    return online === false ? "offline" : "unknown";
}
/**
 * Upload-queue terminal failure → catalogue code. `reason` is the FSM's
 * `defaultClassifyError` string (`http_403: …`, `network_error: …`): a 403 from a presigned S3
 * URL almost always means the signature expired, which only a fresh upload plan fixes.
 */
export function problemCodeForUploadFailure(reason) {
    if (/^http_403\b/.test(reason))
        return "upload_link_expired";
    return "upload_failed";
}
/** Thresholds for {@link problemCodeForRecording}'s stuck detection (spec §5). */
export const STUCK_THRESHOLDS = {
    /** Past `reconcile_stranded`'s 30-minute grace + one 15-minute sweep, a `queued` row should
     * already have been failed server-side; anything older is stuck. */
    queuedMs: 45 * 60 * 1000,
    /** The state machine's hard ceiling is 6h; add an hour of margin. A live capture refreshes
     * `capture_heartbeat_at` every ~2 minutes, so it can never look this old. */
    transcribingMs: 7 * 60 * 60 * 1000,
};
function ageMs(iso, now) {
    if (!iso)
        return null;
    const t = Date.parse(iso);
    return Number.isNaN(t) ? null : now - t;
}
/**
 * The problem a recording row is showing right now, or `null` when it is healthy.
 *
 * - `failed` → its persisted `failure_code`, else `transcription_job_failed` (rows failed before
 *   ERRORS-1 carry no code; the generic copy is still honest for them).
 * - `queued` / `transcribing` past {@link STUCK_THRESHOLDS} → `stuck_in_progress`, but only when
 *   `detectStuck` is set: offering a retry is only useful where the backend accepts one
 *   (`SCRIBE_STALE_RETRY`).
 */
export function problemCodeForRecording(recording, opts = {}) {
    if (recording.status === "failed") {
        return recording.failure_code ?? "transcription_job_failed";
    }
    if (!opts.detectStuck)
        return null;
    const now = opts.now ?? Date.now();
    if (recording.status === "queued") {
        const age = ageMs(recording.transcription_queued_at ?? recording.updated_at, now);
        return age !== null && age > STUCK_THRESHOLDS.queuedMs ? "stuck_in_progress" : null;
    }
    if (recording.status === "transcribing") {
        const newest = [
            recording.capture_heartbeat_at,
            recording.transcription_started_at,
            recording.updated_at,
        ]
            .map((iso) => ageMs(iso, now))
            .filter((a) => a !== null);
        if (newest.length === 0)
            return null;
        return Math.min(...newest) > STUCK_THRESHOLDS.transcribingMs ? "stuck_in_progress" : null;
    }
    return null;
}
//# sourceMappingURL=classify.js.map